import React, { useContext } from "react";
import {
  Box,
  Collapse,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { NavigationBar } from "../../../components/General";
import { ColorModeContext } from "../../../constants/theme";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { states } from "../../../utils/states";
import { useGetLocationICMappedQuery } from "../../../store/ic/icApi";
import FilterButton from "../../../components/Buttons/FilterButton";
import BreadcrumbFont from "../../../components/Typography/BreadcrumbFont";
import {
  GridActionsLayout,
  SearchButton,
  TakeActionButton,
} from "../../../components";
import useDebouncedSearch from "../../../utils/hooks/useDebounceSearch";
import { GridColumns } from "../../../utils/gridColumns";
import { getKey } from "../../../utils/getKey";

const breadcrumbs = [
  <BreadcrumbFont key={1} color="#4B4B4B" text="AGGREGATE IC" />,
  <BreadcrumbFont key={2} color="#005D9E" text="ORGANIZATION LOCATION" last />,
];

function SingleOrgIC() {
  const navigate = useNavigate();
  const url = window.location.href;
  const ID = url.substring(url.lastIndexOf("/") + 1);
  const { mode } = useContext(ColorModeContext);
  const [rows, setRows] = React.useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });
  const { searchText, debouncedResults } = useDebouncedSearch();
  const [selectedStatus, setSelectedStatus] = React.useState("all");
  const [params, setParams] = React.useState({});
  const { data: locData, isFetching } = useGetLocationICMappedQuery({
    ...paginationModel,
    ...searchText,
    ...params,
    status: selectedStatus,
    orgId: ID,
  });
  const [rowCountState, setRowCountState] = React.useState(locData?.count || 0);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    let temp = [];
    if (locData?.locations) {
      temp = locData?.locations?.map((loc) => {
        return {
          id: loc?.id,
          Location: loc?.location_name || "",
          Status: loc?.internal_committee?.status || "PENDING",
        };
      });
    }
    setRows(temp || []);
  }, [locData]);

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      locData?.count ? locData?.count : prevRowCountState,
    );
  }, [locData?.count, setRowCountState]);

  const { handleSubmit, control, watch, resetField } = useForm({
    mode: "onSubmit",
    criteriaMode: "firstError",
    reValidateMode: "onSubmit",
  });
  const selectedState = watch("state");

  React.useEffect(() => {
    resetField("city");
  }, [selectedState]);

  const columns = [
    ...new GridColumns()
      .location()
      .status()
      .actions({
        renderCell: (params) => {
          return (
            <GridActionsLayout>
              <TakeActionButton
                handleClick={() =>
                  navigate(`/superadmin/ic/location/${params.row.id}`)
                }
              />
            </GridActionsLayout>
          );
        },
      })
      .getCols(),
  ];

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const onSubmit = (data) => {
    const params = {
      location_category: data.location_category,
    };
    if (data.state !== "select") {
      params.state = data.state;
    }
    if (data.city && data.city !== "select") {
      params.city = data.city;
    }
    setParams(params);
  };

  return (
    <Box
      flex={1}
      display={"flex"}
      flexDirection={"column"}
      paddingX={"60px"}
      paddingY={"35px"}
      rowGap={"46px"}
      sx={{ background: `${mode === "dark" ? "#0D0D0D" : "#FFFFFF"}` }}
    >
      <NavigationBar breadcrumbs={breadcrumbs} />
      <Box
        display={"flex"}
        flex={1}
        flexDirection={"column"}
        gap={4}
        className="superadmin-data-grid-div"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            padding={"26px"}
            bgcolor={mode === "dark" ? "#151515" : "#EEF8FF"}
            width={"100%"}
            className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-5"
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              <InputLabel>Category</InputLabel>
              <Controller
                name={"location_category"}
                control={control}
                defaultValue={"ic_required"}
                render={({ field: { value, onChange, ref } }) => (
                  <Select
                    sx={{ height: "35px", flex: 1 }}
                    className="tw-border-black  tw-rounded-lg"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                  >
                    <MenuItem value={"ic_required"}>IC Required</MenuItem>
                    <MenuItem value={"ic_not_required"}>
                      IC Not Required
                    </MenuItem>
                  </Select>
                )}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              <InputLabel>State</InputLabel>
              <Controller
                name={"state"}
                control={control}
                defaultValue={"select"}
                render={({ field: { value, onChange, ref } }) => (
                  <Select
                    sx={{ height: "40px", flex: 1 }}
                    className="tw-border-black  tw-rounded-lg"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                  >
                    <MenuItem value={"select"}>Select</MenuItem>
                    {Object.keys(states).map((s, i) => (
                      <MenuItem key={getKey(i)} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              <InputLabel>District</InputLabel>
              <Controller
                name={"city"}
                control={control}
                defaultValue={"select"}
                render={({ field: { value, onChange, ref } }) => (
                  <Select
                    sx={{ height: "40px", flex: 1 }}
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    className="tw-border-black  tw-rounded-lg"
                  >
                    <MenuItem value={"select"}>Select</MenuItem>
                    {states[selectedState]?.map((c, i) => {
                      return (
                        <MenuItem key={getKey(i)} value={c}>
                          {c}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              <SearchButton />
            </Box>
          </Box>
        </form>
        <Stack direction="column" gap={2}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <TextField
              type="text"
              name="search"
              onChange={debouncedResults}
              sx={{
                width: "302px",
                "& .MuiInputBase-root": {
                  height: "32px",
                },
              }}
              placeholder="Search By Company Name"
            />
            <FilterButton
              buttonStyles={{ height: "32px" }}
              onClick={() => setOpen(!open)}
            />
          </Stack>
          <Collapse in={open} timeout="auto" unmountOnExit width="100%">
            <RadioGroup
              row
              name="status"
              value={selectedStatus}
              onChange={handleStatusChange}
              sx={{ justifyContent: "flex-end" }}
            >
              <FormControlLabel value="all" control={<Radio />} label="All" />
              <FormControlLabel
                value="pending"
                control={<Radio />}
                label="Pending"
              />
              <FormControlLabel
                value="completed"
                control={<Radio />}
                label="Completed"
              />
            </RadioGroup>
          </Collapse>
        </Stack>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isFetching}
          disableRowSelectionOnClick
          pageSizeOptions={[5]}
          rowCount={rowCountState}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              background: `${mode === "dark" ? " #181818" : "#EEF8FF"}`,
            },
            "& .MuiDataGrid-toolbarContainer": {
              paddingY: 2,
              paddingX: 1,
            },

            "& .MuiDataGrid-toolbarContainer .MuiButton-root": {
              backgroundColor: "transparent",
              color: "#4B4B4B",
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default SingleOrgIC;
